import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, ChevronLeft, ChevronRight, FileText } from "lucide-react";

// Iter 31.55 — Cold Read history page. Paginated list of past Cold Reads
// (scribe = Two, handler = Ern). Click a row to expand the full markdown.
// Read aging is enforced server-side by the 90-day TTL on db.cold_reads.

const PAGE = 20;

function CardLine({ label, value }) {
    return (
        <span className="label-typed text-[10px] mr-3">
            {label}: <span className="font-heading text-ink">{value}</span>
        </span>
    );
}

function ReadRow({ read, expanded, onToggle, full, loadingFull }) {
    const ts = read.computed_at?.slice(0, 16).replace("T", " ");
    return (
        <li
            className={`paper-card p-4 border-2 ${expanded ? "border-stamp-red" : "border-ink/15 hover:border-ink/40"} transition-colors`}
            data-testid={`cold-read-row-${read.id}`}
        >
            <button
                onClick={onToggle}
                className="w-full text-left"
                data-testid={`cold-read-expand-${read.id}`}
            >
                <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
                    <div className="label-typed tw-prefix">{ts}</div>
                    <div>
                        <CardLine label="scribe" value={read.scribe_key || "—"} />
                        <CardLine label="handler" value={read.handler_key || "—"} />
                        <CardLine label="charged" value={read.charged_count ?? 0} />
                    </div>
                </div>
                <div className="font-body text-sm text-ink-soft line-clamp-3">
                    {read.summary || <span className="italic text-ink-faint">No summary on file.</span>}
                </div>
            </button>

            {expanded && (
                <div className="mt-4 pt-4 border-t border-ink/10" data-testid={`cold-read-full-${read.id}`}>
                    {loadingFull ? (
                        <div className="flex items-center gap-2 text-ink-soft">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span className="font-body text-xs">Pulling full read…</span>
                        </div>
                    ) : (
                        <pre className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed">
                            {full?.markdown || "Full read unavailable."}
                        </pre>
                    )}
                </div>
            )}
        </li>
    );
}

export default function ColdReadHistory() {
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [fullById, setFullById] = useState({});
    const [loadingFullId, setLoadingFullId] = useState("");

    const load = async (p = page) => {
        setErr("");
        try {
            const { data } = await api.get(`/spyhealth/cold-read/history?limit=${PAGE}&offset=${p * PAGE}`);
            setData(data);
        } catch (e) {
            setErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
        }
    };

    useEffect(() => { load(0); /* eslint-disable-next-line */ }, []);

    const onToggle = async (readId) => {
        if (expandedId === readId) {
            setExpandedId(null);
            return;
        }
        setExpandedId(readId);
        if (!fullById[readId]) {
            setLoadingFullId(readId);
            try {
                const { data } = await api.get(`/spyhealth/cold-read/${readId}`);
                setFullById((m) => ({ ...m, [readId]: data }));
            } catch (e) {
                setErr(`[full read ${readId}] ${e?.response?.data?.detail || e.message}`);
            } finally {
                setLoadingFullId("");
            }
        }
    };

    const turnPage = async (dir) => {
        const next = page + dir;
        if (next < 0) return;
        if (dir > 0 && !data?.has_more) return;
        setPage(next);
        await load(next);
    };

    return (
        <Shell>
            <div className="mb-8">
                <div className="label-typed tw-prefix">Spy Health · scribe ledger</div>
                <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1">
                    Cold Read · history<span className="cursor-blink" />
                </h1>
                <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                    Every Cold Read filed by Two, newest first. Click a row to read the
                    full markdown. Reads age out after 90 days on a TTL — pin anything
                    you need to keep into the Ledger.
                </p>
                <div className="mt-3">
                    <Link
                        to="/spy-health"
                        data-testid="cold-read-history-back"
                        className="font-heading uppercase text-xs tracking-[0.22em] text-stamp-red hover:underline"
                    >
                        ← Back to Spy Health
                    </Link>
                </div>
            </div>

            {err && (
                <div className="paper-card border border-stamp-red/40 p-3 mb-4" data-testid="cold-read-history-error">
                    <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words">{err}</pre>
                </div>
            )}

            {!data ? (
                <div className="flex items-center gap-2 text-ink-soft py-12">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="font-body text-sm">Loading history…</span>
                </div>
            ) : data.reads.length === 0 ? (
                <div className="paper-card p-8 text-center" data-testid="cold-read-empty">
                    <FileText className="w-8 h-8 text-ink-faint mx-auto mb-3" />
                    <div className="font-heading uppercase tracking-[0.22em] text-sm text-ink-soft">
                        No Cold Reads on file yet
                    </div>
                    <p className="font-body text-sm text-ink-faint mt-2 max-w-md mx-auto">
                        File a Cold Read from /spy-health to start the trail.
                        Each read costs 3 credits.
                    </p>
                </div>
            ) : (
                <>
                    <div className="flex items-baseline justify-between mb-4">
                        <div className="label-typed text-[11px]">
                            {data.total} total · page {page + 1}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => turnPage(-1)}
                                disabled={page === 0}
                                data-testid="cold-read-prev"
                                className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-2 border border-ink/20 hover:border-stamp-red text-ink-soft hover:text-stamp-red disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                                <ChevronLeft className="w-3 h-3" /> prev
                            </button>
                            <button
                                onClick={() => turnPage(1)}
                                disabled={!data.has_more}
                                data-testid="cold-read-next"
                                className="font-heading uppercase text-[10px] tracking-[0.22em] px-3 py-2 border border-ink/20 hover:border-stamp-red text-ink-soft hover:text-stamp-red disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                            >
                                next <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                    <ul className="space-y-3" data-testid="cold-read-list">
                        {data.reads.map((r) => (
                            <ReadRow
                                key={r.id}
                                read={r}
                                expanded={expandedId === r.id}
                                onToggle={() => onToggle(r.id)}
                                full={fullById[r.id]}
                                loadingFull={loadingFullId === r.id}
                            />
                        ))}
                    </ul>
                </>
            )}
        </Shell>
    );
}
